import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import { setPageTitle } from '../Utils/brand';
import { QuestionCircleIcon, ChevronDownIcon, TelegramIcon, ShieldIcon } from '../Components/Icons';


const faqData = [
    {
        id: 1,
        question: "What is a premium domain and how does it work?",
        answer: "Premium domains are high-value domain names that are either short, memorable, contain popular keywords, or have established traffic. They work just like regular domains but often command higher prices due to their commercial value and branding potential."
    },
    {
        id: 2,
        question: "How do I buy domains on your platform?",
        answer: (
            <span>
                Currently, we support instant payment directly via Telegram using our bot or Telegram Stars. If you are interested in an alternative payment method, please{" "}
                <Link to="/contact.html" className="text-primary-400 hover:text-primary-300 underline font-medium transition-colors">
                    contact us
                </Link>
                .
            </span>
        )
    },
    {
        id: 3,
        question: "Can I rent a domain instead of buying it?",
        answer: "Yes! We offer domain rental options for businesses that need a premium domain temporarily. You can rent domains by the day, month, or year depending on your needs and budget."

    },
    {
        id: 5,
        question: "What are the fees for buying and selling domains?",
        answer: "We charge a 5% platform fee on successful domain sales. For rentals, we take a 10% commission. Payment processing fees may apply depending on your chosen payment method."
    },
    {
        id: 6,
        question: "How do I list my domain for sale?",
        answer: (
            <span>
                Currently, domains are added by our team upon request. To list your domain for sale or rent, please reach out to us via our{" "}
                <Link to="/contact.html" className="text-primary-400 hover:text-primary-300 underline font-medium transition-colors">
                    Contact page
                </Link>
                . We will verify ownership and add it to the marketplace catalog within 24 hours.
            </span>
        )
    },
    {
        id: 7,
        question: "Is it safe to buy domains through your platform?",
        answer: "Yes! We use secure escrow services for all domain transactions. The domain is only transferred to you after payment is confirmed, and we verify domain ownership before any sale is completed."
    },
    {
        id: 8,
        question: "Can I transfer my domain to another registrar?",
        answer: "Absolutely! Once you purchase a domain through our platform, you have full ownership rights and can transfer it to any registrar of your choice. We provide all necessary transfer codes and documentation."
    },
    {
        id: 9,
        question: "How does instant activation work, and what if two users pay simultaneously?",
        answer: "Activation is instant upon payment confirmation, and the domain is assigned to the first confirmed transaction. If two payments are initiated at the same time, the later transaction will simply fail or be canceled, and we will issue an immediate 100% refund."
    }
];

export default function FAQPage() {
    const telegramContactLink = process.env.REACT_APP_TELEGRAM_CONTACT || (process.env.REACT_APP_TELEGRAM_BOT_USERNAME ? `https://t.me/${process.env.REACT_APP_TELEGRAM_BOT_USERNAME}` : 'https://t.me');
    const [openItems, setOpenItems] = useState(new Set());
    const location = useLocation();

    useEffect(() => {
        setPageTitle('Frequently Asked Questions');
    }, []);

    const toggleItem = (id) => {
        const newOpenItems = new Set(openItems);
        if (newOpenItems.has(id)) {
            newOpenItems.delete(id);
        } else {
            newOpenItems.add(id);
        }
        setOpenItems(newOpenItems);
    };

    // Auto-open FAQ item based on URL hash
    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (hash) {
            const itemId = parseInt(hash);
            if (!isNaN(itemId)) {
                setOpenItems(prev => new Set(prev).add(itemId));
                
                // Scroll to the element after a short delay to ensure it's rendered
                setTimeout(() => {
                    const element = document.getElementById(`faq-${itemId}`);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);
            }
        }
    }, [location]);

    return (
        <main className="pt-20">
            <section className="py-20 px-6 md:px-8 lg:px-16">
                <div className="max-w-4xl mx-auto space-y-12">
                    {/* Section Header */}
                    <div className="text-center space-y-3">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-neutral-300 text-xs font-medium tracking-wide">
                            <QuestionCircleIcon className="w-3.5 h-3.5 text-sky-400" />
                            <span>Knowledge Base & Support</span>
                        </div>
                        <h1 className="text-3xl md:text-5xl font-display font-extrabold text-white tracking-tight">
                            Frequently Asked <span className="bg-gradient-to-r from-sky-400 via-indigo-300 to-purple-400 bg-clip-text text-transparent">Questions</span>
                        </h1>
                        <p className="text-sm md:text-base text-neutral-400 max-w-xl mx-auto leading-relaxed">
                            Everything you need to know about domain purchasing, instant rental routing, and forwarding setups.
                        </p>
                    </div>

                    {/* FAQ Accordion */}
                    <div className="space-y-3">
                        {faqData.map((item) => (
                            <FAQItem
                                key={item.id}
                                item={item}
                                isOpen={openItems.has(item.id)}
                                onToggle={() => toggleItem(item.id)}
                                id={`faq-${item.id}`}
                            />
                        ))}
                    </div>

                    {/* Contact Support */}
                    <div className="text-center pt-6">
                        <div className="p-8 max-w-2xl mx-auto rounded-2xl bg-neutral-900/60 border border-white/10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_15px_35px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-5">
                            <div>
                                <h3 className="text-lg font-bold text-white mb-1.5">
                                    Still have questions?
                                </h3>
                                <p className="text-neutral-400 text-xs sm:text-sm">
                                    Can't find the answer you're looking for? Our operations desk is available on Telegram.
                                </p>
                            </div>

                            <div>
                                <a 
                                    href={telegramContactLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="py-3 px-6 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] border border-white/15 text-white text-xs sm:text-sm font-semibold inline-flex items-center gap-2 transition-all shadow-sm active:scale-[0.99]"
                                >
                                    <TelegramIcon className="w-4 h-4 text-sky-400" />
                                    <span>Contact via Telegram</span>
                                </a>
                            </div>

                            <div className="pt-4 border-t border-white/[0.06] text-center">
                                <p className="text-xs text-neutral-400 flex items-center justify-center gap-1.5">
                                    <ShieldIcon className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                                    <span>Always verify official links through this website. We never initiate contact first.</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            
            <Footer />
        </main>
    );
}

const FAQItem = ({ item, isOpen, onToggle, id }) => (
    <div id={id} className="rounded-xl border border-white/10 bg-neutral-900/40 hover:bg-neutral-900/70 overflow-hidden transition-all duration-200">
        <button
            onClick={onToggle}
            className="w-full py-4 text-left flex items-center justify-between transition-colors duration-200"
        >
            <h3 className="text-sm sm:text-base font-semibold text-white px-5 pr-4">
                {item.question}
            </h3>
            <div className={`flex-shrink-0 transition-transform duration-200 px-5 text-neutral-400`}>
                <ChevronDownIcon className={`w-4 h-4 transform transition-transform duration-200 ${isOpen ? 'rotate-180 text-sky-400' : ''}`} />
            </div>
        </button>
        
        <div className={`transition-all duration-200 overflow-hidden ${
            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
            <div className="px-5 pb-5">
                <div className="h-px bg-white/[0.06] mb-3"></div>
                <p className="text-neutral-400 text-xs sm:text-sm leading-relaxed">
                    {item.answer}
                </p>
            </div>
        </div>
    </div>
);